import type { SalesOrderV3Detail } from '@erp/rpc';
import {
  formatCurrency,
  formatPercent,
} from 'src/pages/authenticated/sales-orders/components/line-item-table/priceUtils';
import type { Totals } from '@/pages/authenticated/quote-builder/marginCalc';

type LineItem = SalesOrderV3Detail['lineItems'][number];

function toNum(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : null;
}

const NO_DATA = '—';

/** Resolve a dot-notation path against a line item (for custom metrics). */
function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc != null && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/** Narrow an unknown resolved value to a numeric-coercible type. */
function asNumeric(value: unknown): string | number | null {
  return typeof value === 'number' || typeof value === 'string' ? value : null;
}

/** Apply a computed metric's per-line operator to its two operands. */
function applyOp(left: number, op: string, right: number): number | null {
  switch (op) {
    case 'subtract':
      return left - right;
    case 'add':
      return left + right;
    case 'multiply':
      return left * right;
    case 'divide':
      return right === 0 ? null : left / right;
    case 'percent_off':
      return right === 0 ? null : ((right - left) / right) * 100;
    default:
      return null;
  }
}

/** Format a custom metric's summed value per its format + decimal places. */
function formatMetricValue(
  n: number,
  format: string | undefined,
  decimals: number | undefined,
  currency?: string | null,
): string {
  switch (format) {
    case 'currency':
      return decimals != null
        ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          }).format(n)
        : formatCurrency(n, currency);
    case 'percent':
      return `${n.toFixed(decimals ?? 1)}%`;
    case 'integer':
      return n.toFixed(decimals ?? 0);
    case 'number':
      return decimals != null ? n.toFixed(decimals) : String(n);
    default:
      return decimals != null ? n.toFixed(decimals) : String(n);
  }
}

/** The aggregation/source/format fields a custom metric carries. */
type MetricInput = {
  key: string;
  aggregation?: string;
  format?: string;
  decimals?: number;
  dataPath?: string;
  compute?: { left: string; op: string; right: string };
};

/** Resolve a single line's source value for a custom metric (field or compute). */
function metricLineValue(metric: MetricInput, li: LineItem): number | null {
  if (metric.compute) {
    const left = toNum(asNumeric(getByPath(li, metric.compute.left)));
    const right = toNum(asNumeric(getByPath(li, metric.compute.right)));
    return left != null && right != null
      ? applyOp(left, metric.compute.op, right)
      : null;
  }
  if (metric.dataPath) {
    return toNum(asNumeric(getByPath(li, metric.dataPath)));
  }
  return null;
}

/**
 * Computes a metric's display value from the draft's line data (spec §5.3),
 * mirroring `MarginTotalsSummary`. Cost-based metrics (total margin, margin %,
 * price impact) come from `totals` (the product-margins query, reused via
 * `deriveTotals`); they render "—" when margin data isn't loaded or no line has
 * a cost. `requested ship` still renders "—" until that data is wired.
 *
 * Custom (org-added) metrics carry a `dataPath` (or two-field `compute`) and an
 * `aggregation` — count of populated lines / sum / average — across the lines.
 */
export function computeMetricDisplay(
  metric: MetricInput,
  lines: LineItem[],
  currency?: string | null,
  totals?: Totals,
): string {
  if (metric.dataPath || metric.compute) {
    const aggregation = metric.aggregation ?? 'sum';
    // Count: how many lines have a populated source value (a present field /
    // a computable expression) — not limited to numeric values.
    if (aggregation === 'count') {
      let count = 0;
      for (const li of lines) {
        if (metric.compute) {
          if (metricLineValue(metric, li) != null) count += 1;
        } else if (metric.dataPath) {
          const raw = getByPath(li, metric.dataPath);
          if (raw != null && raw !== '') count += 1;
        }
      }
      return formatMetricValue(count, metric.format, metric.decimals, currency);
    }
    // Sum / Average over the numeric per-line values.
    let sum = 0;
    let n = 0;
    for (const li of lines) {
      const value = metricLineValue(metric, li);
      if (value != null) {
        sum += value;
        n += 1;
      }
    }
    if (n === 0) return NO_DATA;
    return formatMetricValue(
      aggregation === 'average' ? sum / n : sum,
      metric.format,
      metric.decimals,
      currency,
    );
  }

  switch (metric.key) {
    case 'total_revenue':
    case 'total_amount': {
      // One formula everywhere: the stated line amount when present (POs with
      // per-line discounts store the discounted extension there), else
      // qty × unit price — mirrors the backend's computeLineExtension.
      const revenue = lines.reduce((acc, li) => {
        const amount = toNum(li.amount);
        if (amount != null) return acc + amount;
        const qty = toNum(li.quantity);
        const price = toNum(li.unitPrice);
        return qty != null && price != null ? acc + qty * price : acc;
      }, 0);
      return formatCurrency(revenue, currency);
    }
    case 'total_discount_pct': {
      let listRevenue = 0;
      let soldRevenue = 0;
      for (const li of lines) {
        const qty = toNum(li.quantity);
        const price = toNum(li.unitPrice);
        const list = toNum(li.listPrice);
        if (qty == null || price == null || list == null) continue;
        listRevenue += list * qty;
        soldRevenue += price * qty;
      }
      if (listRevenue <= 0) return NO_DATA;
      return formatPercent(((listRevenue - soldRevenue) / listRevenue) * 100);
    }
    case 'line_count':
      return String(lines.length);
    case 'total_margin':
      // `totalMarginPct == null` means no line had a resolved cost.
      return totals && totals.totalMarginPct != null
        ? formatCurrency(totals.totalMargin, currency)
        : NO_DATA;
    case 'total_margin_pct':
      return totals && totals.totalMarginPct != null
        ? formatPercent(totals.totalMarginPct)
        : NO_DATA;
    case 'total_price_impact':
      return totals
        ? formatCurrency(totals.totalPriceImpact, currency)
        : NO_DATA;
    default:
      // e.g. requested ship — not yet available on the relational line.
      return NO_DATA;
  }
}
